import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { UpdateLeaveRequestDTO } from "@/modules/hr/leave/domain/types";
import {
  useLeaveEntitlementsQuery,
  useLeaveRequestDetailQuery,
  useUpdateLeaveRequestMutation,
} from "../api/queries";
import {
  LeaveRequestEditTour,
  useLeaveRequestEditTour,
} from "../tour/LeaveRequestEditTour";
import { LeaveRequestForm } from "../ui/LeaveRequestForm";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const DEFAULT_DETAIL_SEARCH = {
  from: undefined as string | undefined,
  offset: 0,
  limit: 20,
  periodYear: currentYear,
  periodMonth: currentMonth,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
};

type EditSearch = {
  from?: string;
  offset?: number;
  limit?: number;
  periodYear?: number;
  periodMonth?: number;
  status?: string;
  search?: string;
};

function toDetailSearch(s: EditSearch | undefined) {
  if (!s) return DEFAULT_DETAIL_SEARCH;
  return {
    from: s.from,
    offset: s.offset ?? 0,
    limit: s.limit ?? 20,
    periodYear: s.periodYear ?? currentYear,
    periodMonth: s.periodMonth ?? currentMonth,
    status: s.status,
    search: s.search,
  };
}

export function LeaveRequestEditPage() {
  const nav = useNavigate();
  const { id } = useParams({ strict: false });
  const tour = useLeaveRequestEditTour();
  const editSearch = useSearch({ strict: false }) as EditSearch | undefined;
  const detailQuery = useLeaveRequestDetailQuery(id);
  const updateMutation = useUpdateLeaveRequestMutation();
  const request = detailQuery.data;
  const detailSearch = toDetailSearch(editSearch);

  const entitlementYear = request
    ? new Date(request.startDate.slice(0, 10)).getFullYear()
    : 0;
  const entitlementsQuery = useLeaveEntitlementsQuery(
    {
      employeeId: request?.employeeId ?? "",
      leaveTypeId: request?.leaveTypeId ?? "",
      year: entitlementYear,
      limit: 1,
    },
    { enabled: !!request?.employeeId && !!request?.leaveTypeId },
  );
  const remainingDays =
    entitlementsQuery.data?.data?.[0]?.remainingDays ?? null;

  const leaveTypeOptions = useMemo(() => {
    if (!request) return [];
    return [
      {
        value: request.leaveTypeId,
        label: `${request.leaveTypeName} (${request.leaveTypeCode})`,
      },
    ];
  }, [request]);

  const handleBack = () => {
    nav({
      to: "/app/hr/leave/requests/$id",
      params: { id: id ?? "" },
      search: detailSearch,
    });
  };

  const handleSubmit = (values: UpdateLeaveRequestDTO) => {
    if (!id) return;
    updateMutation.mutate(
      { id, dto: values },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກການແກ້ໄຂສຳເລັດ");
          nav({
            to: "/app/hr/leave/requests/$id",
            params: { id },
            search: detailSearch,
          });
        },
        onError: (err: { message?: string }) => {
          toast.error(err?.message ?? "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  if (detailQuery.isLoading || !request) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  if (request.status !== "pending_line") {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">
            ຄຳຂໍລານີ້ບໍ່ສາມາດແກ້ໄຂໄດ້ (ສະຖານະ: {request.status})
          </p>
          <Button
            variant="outline"
            onClick={() =>
              nav({
                to: "/app/hr/leave/requests/$id",
                params: { id: id ?? "" },
                search: detailSearch,
              })
            }
          >
            ກັບຄືນ
          </Button>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <LeaveRequestEditTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="leave-request-edit-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ແກ້ໄຂຄຳຂໍລາ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              {request.leaveTypeName} — {request.startDate.slice(0, 10)} ຫາ{" "}
              {request.endDate.slice(0, 10)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div
          className="mx-auto w-full max-w-2xl space-y-6"
          data-tourid="leave-request-edit-form"
        >
          <LeaveRequestForm
            mode="edit"
            request={request}
            leaveTypeOptions={leaveTypeOptions}
            remainingDays={remainingDays}
            onSubmit={handleSubmit}
            onCancel={handleBack}
            isSubmitting={updateMutation.isPending}
          />
        </div>
      </Main>
    </>
  );
}
